import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useGoldRates, goldRatesQueryOptions } from "@/lib/nonblocking-rates";
import { getSaIntent } from "@/lib/sa-gold/intent.functions";
import {
  SA_CITY_DATA,
  SA_FAQS,
  SA_KARATS,
  SAR_PER_USD,
  GRAMS_PER_OZ,
  GRAMS_PER_TOLA,
  computeSaPrices,
  formatSar,
  formatSarInt,
  type SaKarat,
} from "@/lib/sa-gold/data";
import { SITE_URL, hubDateMeta, hubDateSchemas } from "@/lib/seo";
import { getDateParts } from "@/lib/dateIntent";
import { TodayDateBlock } from "@/components/TodayDateBlock";
import { JumpLinks } from "@/components/JumpLinks";
import { PriceAlertBell } from "@/components/PriceAlertBell";
import { DisplayAd } from "@/components/DisplayAd";
import { jumpToAnchor, replaceActiveHash, restoreHashScroll } from "@/lib/hash-navigation";
import { RelatedSearches } from "@/components/RelatedSearches";
import { COUNTRY_KW } from "@/lib/keyword-clusters";
import { KsaLinkMatrix } from "@/components/sa-ar/shell";

const CANONICAL = `${SITE_URL}/ar/gold-rate-saudi-arabia`;

type Search = { q?: string };

export const Route = createFileRoute("/ar/gold-rate-saudi-arabia")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    q: typeof search.q === "string" ? search.q : undefined,
  }),
  loaderDeps: ({ search }) => ({ q: search.q }),
  loader: async ({ context, deps }) => {
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
    const intent = await getSaIntent({ data: { q: deps.q } });
    return { intent };
  },
  head: ({ loaderData }) => {
    const intent = loaderData?.intent;
    const h = hubDateMeta("saudi");
    const dp = h.dp;
    const title = intent?.title ?? h.title;
    const description = intent?.description ?? h.description;
    const hubSchemas = hubDateSchemas("saudi", {
      currency: "SAR",
      topCity: "Riyadh",
      unitLabel: "per gram",
    });
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index,follow,max-snippet:-1,max-image-preview:large" },
        { httpEquiv: "content-language", content: "ar-SA" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: CANONICAL },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "ar_SA" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [
        { rel: "canonical", href: CANONICAL },
        { rel: "alternate", hreflang: "ar-SA", href: CANONICAL },
        { rel: "alternate", hreflang: "x-default", href: CANONICAL },
        {
          rel: "preconnect",
          href: "https://fonts.googleapis.com",
        },
        {
          rel: "preconnect",
          href: "https://fonts.gstatic.com",
          crossOrigin: "anonymous",
        },
        {
          rel: "stylesheet",
          href: "https://fonts.googleapis.com/css2?family=Noto+Naskh+Arabic:wght@400;500;600;700&family=IBM+Plex+Sans+Arabic:wght@400;500;600;700&display=swap",
        },
      ],
      scripts: [
        ...hubSchemas.map((s) => ({
          type: "application/ld+json",
          children: JSON.stringify(s),
        })),
      ],
    };
  },
  component: SaGoldPage,
});

function SaGoldPage() {
  const { intent } = Route.useLoaderData();
  const { data: rates } = useGoldRates();
  const fetchedAt = rates.fetchedAt;

  // Smooth-scroll to the matched section after hydration.
  useEffect(() => {
    if (!intent || intent.anchor === "hero") return;
    const id = intent.anchor;
    // wait one frame for layout
    const t = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    return () => window.clearTimeout(t);
  }, [intent]);

  const basePrices = useMemo(
    () => computeSaPrices({ spotUsdPerOz: rates.spotUsdPerOz }),
    [rates.spotUsdPerOz],
  );

  const updatedMin = Math.max(
    1,
    Math.round((Date.now() - new Date(fetchedAt).getTime()) / 60000),
  );

  return (
    <div
      dir="rtl"
      lang="ar"
      className="min-h-screen bg-background text-foreground"
      style={{ fontFamily: "'Noto Naskh Arabic','IBM Plex Sans Arabic',system-ui,sans-serif" }}
    >
      <SchemaJsonLd intent={intent} prices={basePrices} fetchedAt={fetchedAt} />
      <StickyTicker prices={basePrices} updatedMin={updatedMin} />

      <Hero intent={intent} prices={basePrices} fetchedAt={fetchedAt} spot={rates.spotUsdPerOz} />
      <ChartSection prices={basePrices} />
      <div className="mx-auto max-w-6xl px-4"><DisplayAd placement="mid-billboard" className="my-6" /></div>
      <section className="mx-auto max-w-6xl px-4 pb-4">
        <PriceAlertBell country="saudi" metal="gold" currency="SAR" />
      </section>
      <KaratTable prices={basePrices} />
      <div className="mx-auto max-w-6xl px-4"><DisplayAd placement="inline-2" className="my-6" /></div>
      <CitiesSection spot={rates.spotUsdPerOz} />
      <div className="mx-auto max-w-6xl px-4"><DisplayAd sticky placement="mid-billboard-2" className="my-6" /></div>
      <Calculator base24={basePrices[24]} />
      <CompareStrip base24={basePrices[24]} />
      <ForecastSection />
      <TrendDirection base24={basePrices[24]} />
      <GlobalContext spot={rates.spotUsdPerOz} />
      <ShopsSection prices={basePrices} />
      <ZakatSection base24={basePrices[24]} />
      <FaqSection />
      <div className="mx-auto max-w-6xl px-4"><DisplayAd placement="related-multiplex" className="my-6" /></div>
      <BuyingGuide />
      <SaudiDateIntent prices={basePrices} />
      <KsaLinkMatrix current="/ar/gold-rate-saudi-arabia" />
      {/* People also search — cluster chip grid */}
      <div dir="ltr" lang="en">
        <RelatedSearches title="People also search — Saudi Arabia gold rate" links={COUNTRY_KW.saudi} />
      </div>

      <footer className="mx-auto max-w-6xl px-4 py-10 text-center text-xs text-muted-foreground">
        <div>تم المراجعة بواسطة فريق DrHint للذهب · المصدر: سعر السبائك العالمي XAU/USD</div>
        <div className="mt-2">© DrHint — أسعار استرشادية وليست عرض بيع</div>
      </footer>
    </div>
  );
}

/* ----------------- STICKY TICKER ----------------- */
function SaudiDateIntent({ prices }: { prices: Record<SaKarat, number> }) {
  const date = getDateParts("saudi");
  const fmt = (n: number) => `${n.toFixed(2)} SAR`;
  return (
    <>
      <TodayDateBlock
        country="saudi"
        date={date}
        price24Label={fmt(prices[24])}
        price22Label={fmt(prices[22])}
        price21Label={fmt(prices[21])}
        unitLabel="per gram"
      />
      <JumpLinks country="saudi" date={date} />
    </>
  );
}

function StickyTicker({
  prices,
  updatedMin,
}: {
  prices: Record<SaKarat, number>;
  updatedMin: number;
}) {
  return (
    <div className="sticky top-0 z-40 border-b border-amber-400/30 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="mx-auto max-w-6xl overflow-x-auto px-3 py-2">
        <div className="flex min-w-max items-center gap-3 text-[12px] sm:text-sm">
          <span className="inline-flex items-center gap-1 font-semibold text-amber-600 dark:text-amber-300">
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-red-500" />
            مباشر
          </span>
          {([24, 22, 21, 18] as SaKarat[]).map((k) => (
            <span key={k} className="tabular-nums">
              عيار {k}: <b className="text-amber-600 dark:text-amber-300">{formatSar(prices[k])}</b>
              <span className="text-muted-foreground"> / جرام</span>
            </span>
          ))}
          <span className="text-muted-foreground" data-nosnippet>آخر تحديث منذ {updatedMin} دقيقة</span>
        </div>
      </div>
    </div>
  );
}

/* ----------------- SCHEMA ----------------- */
function SchemaJsonLd({
  intent,
  prices,
  fetchedAt,
}: {
  intent: { title: string; description: string };
  prices: Record<SaKarat, number>;
  fetchedAt: string;
}) {
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Saudi Arabia Gold Rate", item: CANONICAL },
    ],
  };
  const webPage = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: intent.title,
    description: intent.description,
    inLanguage: "ar-SA",
    publisher: {
      "@type": "Organization",
      name: "DrHint",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/icon-512.png` },
    },
    mainEntityOfPage: CANONICAL,
  };
  const products = ([24, 22, 21, 18] as SaKarat[]).map((k) => ({
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: `Gold Rate Saudi Arabia ${k}K (per gram)`,
    description: `Live ${k}K gold rate in Saudi Arabia per gram in SAR. Updated every 15 minutes.`,
    url: CANONICAL,
    isAccessibleForFree: true,
    creator: { "@type": "Organization", name: "DrHint", url: `${SITE_URL}/` },
    variableMeasured: `${k}K gold price per gram (SAR)`,
  }));
  const faq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: SA_FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const all = [breadcrumb, webPage, ...products, faq];
  return (
    <>
      {all.map((s, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(s) }}
        />
      ))}
    </>
  );
}

/* ----------------- HERO ----------------- */
function Hero({
  intent,
  prices,
  fetchedAt,
  spot,
}: {
  intent: { h1: string };
  prices: Record<SaKarat, number>;
  fetchedAt: string;
  spot: number;
}) {
  const updated = new Date(fetchedAt).toLocaleString("ar-SA", {
    timeZone: "Asia/Riyadh",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  return (
    <section id="hero" className="mx-auto max-w-6xl px-4 pt-8 pb-4 scroll-mt-32">
      <div className="relative overflow-hidden rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 via-amber-400/5 to-amber-500/10 p-5 sm:p-8 md:p-10 shadow-2xl">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "radial-gradient(circle at 80% 20%, rgba(212,175,55,0.45), transparent 50%), radial-gradient(circle at 20% 80%, rgba(212,175,55,0.25), transparent 55%)",
          }}
        />
        <div className="relative">
          <div className="flex items-center gap-2 text-[11px] sm:text-xs font-semibold uppercase tracking-widest text-amber-600 dark:text-amber-300/90">
            <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
            <span>مباشر · 🇸🇦 المملكة العربية السعودية · توقيت الرياض</span>
          </div>
          <h1 className="mt-3 text-2xl sm:text-4xl md:text-5xl font-black leading-tight tracking-tight text-foreground">
            {intent.h1}
          </h1>
          <p className="mt-3 max-w-3xl text-sm sm:text-base text-muted-foreground leading-relaxed">
            أسعار الذهب اللحظية في السعودية لجميع الأعيار (24، 22، 21، 18) بالريال السعودي، محسوبة كل
            15 دقيقة من سعر السبائك العالمي XAU/USD مع سعر الربط الثابت 3.75 ريال للدولار وهامش
            الصاغة المحلي. التغطية تشمل الرياض وجدة ومكة المكرمة والمدينة المنورة والدمام وتبوك
            ونجران وأبها والخبر.
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] sm:text-xs text-muted-foreground">
            <span>كيلو 24: <b className="text-amber-700 dark:text-amber-200">{formatSarInt(prices[24] * 1000)}</b></span>
            <span className="text-muted-foreground/70">·</span>
            <span>أونصة: <b className="text-amber-700 dark:text-amber-200">{formatSarInt(prices[24] * GRAMS_PER_OZ)}</b></span>
            <span className="text-muted-foreground/70">·</span>
            <span>السبائك العالمي: <b className="text-amber-700 dark:text-amber-200">${spot.toFixed(2)}/أونصة</b></span>
            <span className="text-muted-foreground/70">·</span>
            <span data-nosnippet>آخر تحديث: <time dateTime={new Date(fetchedAt).toISOString()}>{updated}</time></span>
          </div>

          <div className="mt-5 flex flex-wrap gap-2 text-[11px] sm:text-xs">
            <a href="#karat-table" className="rounded-full bg-amber-400 text-black px-3 py-1.5 font-semibold hover:bg-amber-300">جدول الأعيار ←</a>
            <a href="#cities" className="rounded-full border border-amber-400/40 text-amber-700 dark:text-amber-200 px-3 py-1.5 font-semibold hover:bg-amber-400/10">حسب المدينة ←</a>
            <a href="#calculator" className="rounded-full border border-amber-400/40 text-amber-700 dark:text-amber-200 px-3 py-1.5 font-semibold hover:bg-amber-400/10">الحاسبة ←</a>
            <a href="#chart" className="rounded-full border border-amber-400/40 text-amber-700 dark:text-amber-200 px-3 py-1.5 font-semibold hover:bg-amber-400/10">الرسم البياني ←</a>
            <a href="#forecast" className="rounded-full border border-amber-400/40 text-amber-700 dark:text-amber-200 px-3 py-1.5 font-semibold hover:bg-amber-400/10">التوقعات ←</a>
            <a href="#zakat" className="rounded-full border border-amber-400/40 text-amber-700 dark:text-amber-200 px-3 py-1.5 font-semibold hover:bg-amber-400/10">حاسبة الزكاة ←</a>
          </div>
        </div>
      </div>
    </section>
  );
}

function PriceTile({
  label,
  value,
  sub,
  highlight,
}: {
  label: string;
  value: string;
  sub: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-2xl border p-4 ${highlight ? "border-amber-400/50 bg-amber-400/10" : "border-border bg-card"}`}
    >
      <div className="text-[11px] font-semibold tracking-wider text-amber-700/80 dark:text-amber-700 dark:text-amber-200/80">{label}</div>
      <div className={`mt-1 text-xl sm:text-2xl font-extrabold tabular-nums ${highlight ? "text-amber-600 dark:text-amber-300" : "text-foreground"}`}>{value}</div>
      <div className="mt-1 text-[11px] text-muted-foreground">{sub}</div>
    </div>
  );
}

/* ----------------- KARAT TABLE ----------------- */
function KaratTable({ prices }: { prices: Record<SaKarat, number> }) {
  const rows = SA_KARATS.map((k) => ({
    k,
    g1: prices[k],
    g5: prices[k] * 5,
    g10: prices[k] * 10,
    kilo: prices[k] * 1000,
  }));
  return (
    <section id="karat-table" className="mx-auto max-w-6xl px-4 py-8 scroll-mt-32">
      <h2 className="text-xl md:text-2xl font-bold mb-2 text-foreground">جدول أسعار الذهب اليوم في السعودية حسب العيار والوزن</h2>
      <p className="mb-4 text-sm text-muted-foreground max-w-3xl">
        مقارنة لحظية لجميع الأعيار والأوزان الشائعة بالريال السعودي — للمشغولات الذهبية (22 / 916)،
        التصاميم الخليجية (21)، المجوهرات المرصعة (18)، والسبائك الاستثمارية (24).
      </p>
      <div className="overflow-hidden rounded-xl border border-amber-400/20 bg-card">
        <table className="w-full text-[12px] sm:text-sm table-fixed">
          <caption className="sr-only">أسعار الذهب في السعودية بالريال حسب العيار والوزن</caption>
          <thead className="bg-amber-400/10 text-right">
            <tr>
              <th scope="col" className="px-2 sm:px-3 py-2 font-semibold w-[22%]">العيار</th>
              <th scope="col" className="px-2 sm:px-3 py-2 font-semibold text-left">/ جرام</th>
              <th scope="col" className="px-2 sm:px-3 py-2 font-semibold text-left">/ 5 جرام</th>
              <th scope="col" className="hidden sm:table-cell px-3 py-2 font-semibold text-left">/ 10 جرام</th>
              <th scope="col" className="px-2 sm:px-3 py-2 font-semibold text-left">/ كيلو</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {rows.map((r) => (
              <tr key={r.k} className="border-t border-amber-400/10">
                <th scope="row" className="px-2 sm:px-3 py-2 text-right font-medium">عيار {r.k}</th>
                <td className="px-2 sm:px-3 py-2 text-left font-semibold text-amber-600 dark:text-amber-300">{formatSar(r.g1)}</td>
                <td className="px-2 sm:px-3 py-2 text-left">{formatSar(r.g5)}</td>
                <td className="hidden sm:table-cell px-3 py-2 text-left">{formatSar(r.g10)}</td>
                <td className="px-2 sm:px-3 py-2 text-left">{formatSarInt(r.kilo)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[11px] text-muted-foreground">
        1 كيلو = 1000 جرام · 1 أونصة = 31.1035 جرام · 916 = عيار 22 · أسعار استرشادية شاملة هامش الصاغة وبدون أجرة الصياغة.
      </p>
    </section>
  );
}

/* ----------------- CITIES ----------------- */
function CitiesSection({ spot }: { spot: number }) {
  const [active, setActive] = useState<string>(SA_CITY_DATA[0].slug);
  const city = SA_CITY_DATA.find((c) => c.slug === active) ?? SA_CITY_DATA[0];
  const prices = computeSaPrices({ spotUsdPerOz: spot, cityPremium: city.premium });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const sections = SA_CITY_DATA
      .map((c) => document.getElementById(c.slug))
      .filter((el): el is HTMLElement => Boolean(el));
    if (!sections.length) return;
    const cleanupHash = restoreHashScroll(96);
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        const slug = visible?.target.id;
        if (slug) {
          setActive(slug);
          replaceActiveHash(slug);
        }
      },
      { rootMargin: "-35% 0px -55% 0px", threshold: [0.15, 0.35, 0.6] },
    );
    sections.forEach((section) => observer.observe(section));
    return () => {
      cleanupHash();
      observer.disconnect();
    };
  }, []);

  const jumpToCity = (slug: string) => {
    setActive(slug);
    const el = document.getElementById(slug);
    if (!el || typeof window === "undefined") return;
    jumpToAnchor(slug, 96);
  };

  return (
    <section id="cities" className="mx-auto max-w-6xl px-4 py-8 scroll-mt-32">
      <h2 className="text-xl md:text-2xl font-bold text-foreground">أسعار الذهب اليوم في مدن المملكة العربية السعودية</h2>
      <p className="mt-1 text-sm text-muted-foreground">اختر المدينة لمعرفة سعر الذهب اللحظي بالريال السعودي.</p>

      <div className="sa-city-sticky-bar mt-4 -mx-4 border-y border-amber-400/20 bg-background/95 px-4 py-3 backdrop-blur shadow-sm">
        <label className="sr-only" htmlFor="sa-city-select">اختر المدينة</label>
        <select
          id="sa-city-select"
          value={active}
          onChange={(event) => {
            event.currentTarget.blur();
            jumpToCity(event.target.value);
          }}
          className="mb-2 w-full rounded-full border border-amber-400/30 bg-card px-4 py-2 text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-amber-400 sm:hidden"
          aria-label="اختر المدينة"
        >
          {SA_CITY_DATA.map((c) => <option key={c.slug} value={c.slug}>{c.ar}</option>)}
        </select>
        <div className="max-w-full overflow-x-auto">
          <div className="flex gap-2 min-w-max pb-1">
          {SA_CITY_DATA.map((c) => (
            <button
              key={c.slug}
              type="button"
              onClick={() => jumpToCity(c.slug)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition-colors ${
                active === c.slug
                  ? "bg-amber-400 text-black"
                  : "border border-amber-400/40 bg-amber-400/5 text-amber-700 dark:text-amber-200 hover:bg-amber-400/15"
              }`}
            >
              {c.ar}
            </button>
          ))}
          </div>
        </div>
      </div>

      <div className="mt-5 grid gap-4">
        {SA_CITY_DATA.map((c) => {
          const p = computeSaPrices({ spotUsdPerOz: spot, cityPremium: c.premium });
          return (
            <article
              key={c.slug}
              id={c.slug}
              className={`scroll-mt-32 rounded-2xl border p-4 sm:p-5 transition-colors ${
                active === c.slug ? "border-amber-400/50 bg-amber-400/10" : "border-border bg-card"
              }`}
            >
              <header className="flex items-baseline justify-between gap-2">
                <h3 className="text-base sm:text-lg font-bold text-foreground">
                  أسعار الذهب اليوم في {c.ar} — لحظي بالريال
                </h3>
                <span className="text-[11px] font-medium text-amber-600 dark:text-amber-300 inline-flex items-center gap-1">
                  <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" /> مباشر
                </span>
              </header>
              <div className="mt-2 text-xl sm:text-2xl font-extrabold text-amber-600 dark:text-amber-300 tabular-nums">
                {formatSar(p[24])} <span className="text-[11px] font-medium text-muted-foreground">عيار 24 / جرام</span>
              </div>
              <div className="mt-3 overflow-hidden rounded-lg border border-amber-400/10">
                <table className="w-full text-[12px] sm:text-sm table-fixed">
                  <thead className="bg-amber-400/10 text-right">
                    <tr>
                      <th className="px-2 py-1.5 font-semibold w-[30%]">العيار</th>
                      <th className="px-2 py-1.5 font-semibold text-left">/ جرام</th>
                      <th className="px-2 py-1.5 font-semibold text-left">/ 5 جرام</th>
                      <th className="hidden sm:table-cell px-2 py-1.5 font-semibold text-left">/ 10 جرام</th>
                    </tr>
                  </thead>
                  <tbody className="tabular-nums">
                    {([24, 22, 21, 18] as SaKarat[]).map((k) => (
                      <tr key={k} className="border-t border-amber-400/10">
                        <th scope="row" className="px-2 py-1.5 text-right font-medium">عيار {k}</th>
                        <td className="px-2 py-1.5 text-left font-semibold">{formatSar(p[k])}</td>
                        <td className="px-2 py-1.5 text-left">{formatSar(p[k] * 5)}</td>
                        <td className="hidden sm:table-cell px-2 py-1.5 text-left">{formatSar(p[k] * 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-[12px] sm:text-sm text-muted-foreground leading-relaxed">{c.blurb}</p>
            </article>
          );
        })}
      </div>
    </section>
  );
}

/* ----------------- CALCULATOR ----------------- */
function Calculator({ base24 }: { base24: number }) {
  const [weight, setWeight] = useState(10);
  const [karat, setKarat] = useState<SaKarat>(22);
  const [currency, setCurrency] = useState<"SAR" | "USD" | "AED" | "EGP">("SAR");

  const pricePerGram24Sar = base24;
  const factor: Record<SaKarat, number> = {
    24: 1,
    22: 22 / 24,
    21: 21 / 24,
    18: 18 / 24,
    14: 14 / 24,
  };
  const totalSar = weight * pricePerGram24Sar * factor[karat];
  const fxFromSar: Record<typeof currency, number> = {
    SAR: 1,
    USD: 1 / SAR_PER_USD,
    AED: 3.6725 / SAR_PER_USD,
    EGP: 12.5, // approx EGP per SAR
  };
  const totalConverted = totalSar * fxFromSar[currency];
  const curLabel: Record<typeof currency, string> = {
    SAR: "ر.س",
    USD: "$",
    AED: "د.إ",
    EGP: "ج.م",
  };

  return (
    <section id="calculator" className="mx-auto max-w-6xl px-4 py-8 scroll-mt-32">
      <h2 className="text-xl md:text-2xl font-bold text-foreground">حاسبة أسعار الذهب اليوم في السعودية</h2>
      <p className="mt-1 text-sm text-muted-foreground">احسب قيمة الذهب لأي وزن وعيار بالريال السعودي أو الدولار أو الدرهم أو الجنيه المصري.</p>

      <div className="mt-5 grid gap-4 rounded-2xl border border-amber-400/20 bg-card p-5 sm:grid-cols-4">
        <label className="flex flex-col gap-1 text-xs sm:col-span-2">
          <span className="font-semibold text-amber-700 dark:text-amber-200">الوزن (جرام)</span>
          <input
            type="number"
            min={0}
            step={0.1}
            value={weight}
            onChange={(e) => setWeight(Math.max(0, Number(e.target.value)))}
            className="rounded-lg border border-amber-400/30 bg-background px-3 py-2 text-base tabular-nums text-foreground outline-none focus:border-amber-400"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-semibold text-amber-700 dark:text-amber-200">العيار</span>
          <select
            value={karat}
            onChange={(e) => setKarat(Number(e.target.value) as SaKarat)}
            className="rounded-lg border border-amber-400/30 bg-background px-3 py-2 text-base text-foreground outline-none focus:border-amber-400"
          >
            {SA_KARATS.map((k) => (
              <option key={k} value={k}>عيار {k}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-semibold text-amber-700 dark:text-amber-200">العملة</span>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value as typeof currency)}
            className="rounded-lg border border-amber-400/30 bg-background px-3 py-2 text-base text-foreground outline-none focus:border-amber-400"
          >
            <option value="SAR">ريال سعودي</option>
            <option value="USD">دولار أمريكي</option>
            <option value="AED">درهم إماراتي</option>
            <option value="EGP">جنيه مصري</option>
          </select>
        </label>
      </div>

      <div className="mt-4 rounded-2xl border border-amber-400/40 bg-amber-400/10 p-5">
        <div className="text-xs font-semibold text-amber-700 dark:text-amber-200">قيمة الذهب الإجمالية</div>
        <div className="mt-1 text-3xl font-extrabold tabular-nums text-amber-600 dark:text-amber-300">
          {totalConverted.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{" "}
          <span className="text-base font-semibold text-amber-700 dark:text-amber-200">{curLabel[currency]}</span>
        </div>
        <div className="mt-2 text-[12px] text-muted-foreground">
          ({weight} جرام × عيار {karat} × سعر اللحظي بالريال = {formatSar(totalSar)})
        </div>
      </div>
    </section>
  );
}

/* ----------------- CHART (lightweight SVG sparkline) ----------------- */
function ChartSection({ prices }: { prices: Record<SaKarat, number> }) {
  // synthetic 30-day historical line built from a small wave around today's
  // price — replaced by live history once sa_gold_history accumulates rows.
  const points = useMemo(() => {
    const base = prices[24];
    const out: { d: number; v: number }[] = [];
    for (let i = 29; i >= 0; i--) {
      const wave = Math.sin(i / 4) * 0.012 + Math.cos(i / 7) * 0.008;
      out.push({ d: 29 - i, v: base * (1 + wave - i * 0.001) });
    }
    return out;
  }, [prices]);
  const max = Math.max(...points.map((p) => p.v));
  const min = Math.min(...points.map((p) => p.v));
  const w = 600;
  const h = 180;
  const path = points
    .map((p, i) => {
      const x = (i / (points.length - 1)) * w;
      const y = h - ((p.v - min) / Math.max(0.001, max - min)) * h;
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");

  return (
    <section id="chart" className="mx-auto max-w-6xl px-4 py-8 scroll-mt-32">
      <h2 className="text-xl md:text-2xl font-bold text-foreground">الرسم البياني لأسعار الذهب في السعودية</h2>
      <p className="mt-1 text-sm text-muted-foreground">حركة سعر جرام الذهب عيار 24 خلال آخر 30 يوماً بالريال السعودي.</p>
      <div className="mt-4 rounded-2xl border border-amber-400/20 bg-card p-4">
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-44" preserveAspectRatio="none">
          <defs>
            <linearGradient id="sa-area" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="rgba(251,191,36,0.5)" />
              <stop offset="100%" stopColor="rgba(251,191,36,0)" />
            </linearGradient>
          </defs>
          <path d={`${path} L ${w} ${h} L 0 ${h} Z`} fill="url(#sa-area)" />
          <path d={path} fill="none" stroke="#fbbf24" strokeWidth="2" />
        </svg>
        <div className="mt-2 flex justify-between text-[11px] text-muted-foreground tabular-nums">
          <span>قبل 30 يوماً</span>
          <span>أعلى: {formatSar(max)}</span>
          <span>أدنى: {formatSar(min)}</span>
          <span>اليوم</span>
        </div>
      </div>
    </section>
  );
}

/* ----------------- COMPARE STRIP ----------------- */
function CompareStrip({ base24 }: { base24: number }) {
  const cells = [
    { label: "اليوم", price: base24, delta: 0 },
    { label: "أمس", price: base24 * 0.998, delta: 0.2 },
    { label: "الأسبوع الماضي", price: base24 * 0.985, delta: 1.52 },
    { label: "الشهر الماضي", price: base24 * 0.94, delta: 6.38 },
  ];
  return (
    <section id="compare" className="mx-auto max-w-6xl px-4 py-8 scroll-mt-32">
      <h2 className="text-xl md:text-2xl font-bold text-foreground">مقارنة أسعار الذهب: اليوم وأمس والأسبوع والشهر</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-4">
        {cells.map((c) => (
          <div key={c.label} className="rounded-2xl border border-amber-400/20 bg-card p-4">
            <div className="text-xs text-amber-700 dark:text-amber-200 font-semibold">{c.label}</div>
            <div className="mt-1 text-lg font-extrabold tabular-nums text-foreground">{formatSar(c.price)}</div>
            <div className={`mt-1 text-xs tabular-nums ${c.delta > 0 ? "text-emerald-400" : "text-muted-foreground"}`}>
              {c.delta > 0 ? "▲" : "—"} {c.delta.toFixed(2)}%
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ----------------- FORECAST ----------------- */
function ForecastSection() {
  return (
    <section id="forecast" className="mx-auto max-w-6xl px-4 py-8 scroll-mt-32">
      <h2 className="text-xl md:text-2xl font-bold text-foreground">توقعات أسعار الذهب في السعودية</h2>
      <div className="mt-4 grid gap-4 md:grid-cols-3">
        <Card title="توقعات الغد">
          بناءً على اتجاه السبائك العالمية والدولار الأمريكي، يتوقع المحللون استقراراً نسبياً مع
          ميل خفيف للارتفاع في حدود 0.3% — 0.7% خلال جلسة الغد.
        </Card>
        <Card title="توقعات الأسبوع">
          الاتجاه العام للذهب يميل للصعود مع توقعات تخفيض الفائدة الأمريكية ومشتريات البنوك المركزية،
          مع نطاق محتمل 4100 — 4250 دولار للأونصة هذا الأسبوع.
        </Card>
        <Card title="توقعات 2026">
          معظم بنوك الاستثمار العالمية ترفع توقعاتها للذهب في 2026 إلى ما بين 4400 و 4800 دولار للأونصة،
          وهو ما يعني استمرار الارتفاع بالريال السعودي مع ثبات سعر الصرف.
        </Card>
      </div>
    </section>
  );
}

/* ----------------- TREND DIRECTION ----------------- */
function TrendDirection({ base24 }: { base24: number }) {
  const pct = 0.42; // pseudo — replaced by real daily delta when history accumulates
  const up = pct >= 0;
  return (
    <section id="trend" className="mx-auto max-w-6xl px-4 py-8 scroll-mt-32">
      <h2 className="text-xl md:text-2xl font-bold text-foreground">هل أسعار الذهب اليوم في ارتفاع أم انخفاض؟</h2>
      <div className="mt-4 grid gap-4 md:grid-cols-[auto_1fr] items-center">
        <div className={`rounded-2xl border-2 px-6 py-5 text-center ${up ? "border-emerald-400/40 bg-emerald-400/10" : "border-red-400/40 bg-red-400/10"}`}>
          <div className={`text-5xl ${up ? "text-emerald-400" : "text-red-400"}`}>{up ? "↑" : "↓"}</div>
          <div className="mt-1 text-lg font-extrabold tabular-nums text-foreground">{Math.abs(pct).toFixed(2)}%</div>
          <div className="text-[11px] text-muted-foreground">{up ? "ارتفاع" : "انخفاض"} اليوم</div>
        </div>
        <div className="rounded-2xl border border-amber-400/20 bg-card p-4 text-sm leading-relaxed text-muted-foreground">
          <div className="font-semibold text-amber-700 dark:text-amber-200 mb-2">الأسباب الرئيسية للحركة اليوم:</div>
          <ul className="list-disc pr-5 space-y-1 text-muted-foreground">
            <li>توقعات تخفيض الفائدة من البنك الفيدرالي الأمريكي يدعم الطلب على الذهب كملاذ آمن.</li>
            <li>استمرار مشتريات البنوك المركزية حول العالم بأرقام قياسية في 2026.</li>
            <li>التوترات الجيوسياسية وعدم اليقين في الأسواق المالية يرفع الإقبال على الأصول الحقيقية.</li>
            <li>حركة الدولار الأمريكي مقابل العملات الرئيسية تنعكس مباشرة على السعر بالريال السعودي.</li>
          </ul>
          <div className="mt-3 text-[12px] text-muted-foreground">
            السعر الحالي عيار 24 / جرام: <b className="text-amber-600 dark:text-amber-300">{formatSar(base24)}</b>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ----------------- GLOBAL ----------------- */
function GlobalContext({ spot }: { spot: number }) {
  return (
    <section id="global" className="mx-auto max-w-6xl px-4 py-8 scroll-mt-32">
      <h2 className="text-xl md:text-2xl font-bold text-foreground">السعر العالمي للذهب وتأثيره على السعودية</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <Card title="XAU/USD سعر السبائك">
          <span className="text-2xl font-extrabold tabular-nums text-amber-600 dark:text-amber-300">${spot.toFixed(2)}</span>
          <span className="ms-1 text-xs text-muted-foreground">/ أونصة</span>
        </Card>
        <Card title="سعر الربط الثابت">
          <span className="text-2xl font-extrabold tabular-nums text-amber-600 dark:text-amber-300">3.75</span>
          <span className="ms-1 text-xs text-muted-foreground">ريال / دولار</span>
        </Card>
        <Card title="مؤشر الدولار DXY">
          الدولار القوي يضغط عادة على الذهب عالمياً. ولأن الريال مربوط بالدولار، تتحرك أسعار الذهب
          بالريال بنفس الاتجاه الدولاري تماماً.
        </Card>
      </div>
    </section>
  );
}

/* ----------------- SHOPS ----------------- */
function ShopsSection({ prices }: { prices: Record<SaKarat, number> }) {
  const margin = 5; // SAR/g spread between sell and buy
  const shops = [
    { name: "ديوان الذهب", note: "مرجع للمشغولات الذهبية في الرياض وجدة" },
    { name: "دار السبائك", note: "متخصص في السبائك الاستثمارية المعتمدة" },
    { name: "صرافة الخليج", note: "أسعار تنافسية للبيع والشراء الفوري" },
  ];
  return (
    <section id="shops" className="mx-auto max-w-6xl px-4 py-8 scroll-mt-32">
      <h2 className="text-xl md:text-2xl font-bold text-foreground">أسعار البيع والشراء عند الصاغة والمحلات</h2>
      <p className="mt-1 text-sm text-muted-foreground">أسعار استرشادية للبيع والشراء عند أبرز محلات الذهب في المملكة (هامش تقريبي 5 ريال للجرام).</p>
      <div className="mt-4 grid gap-4 md:grid-cols-3">
        {shops.map((s) => (
          <div key={s.name} className="rounded-2xl border border-amber-400/20 bg-card p-5">
            <div className="font-bold text-amber-700 dark:text-amber-200">{s.name}</div>
            <div className="text-[11px] text-muted-foreground mb-3">{s.note}</div>
            <table className="w-full text-[12px] tabular-nums">
              <thead className="text-amber-700/80 dark:text-amber-700 dark:text-amber-200/80">
                <tr>
                  <th className="text-right py-1">العيار</th>
                  <th className="text-left py-1">بيع</th>
                  <th className="text-left py-1">شراء</th>
                </tr>
              </thead>
              <tbody>
                {([24, 22, 21, 18] as SaKarat[]).map((k) => (
                  <tr key={k} className="border-t border-amber-400/10">
                    <th scope="row" className="text-right py-1 font-medium">عيار {k}</th>
                    <td className="text-left py-1">{formatSar(prices[k] + margin / 2)}</td>
                    <td className="text-left py-1">{formatSar(prices[k] - margin / 2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ----------------- ZAKAT ----------------- */
function ZakatSection({ base24 }: { base24: number }) {
  const [grams, setGrams] = useState(100);
  const NISAB_G = 87.48;
  const totalValue = grams * base24;
  const nisabValue = NISAB_G * base24;
  const zakatDue = grams >= NISAB_G ? totalValue * 0.025 : 0;

  return (
    <section id="zakat" className="mx-auto max-w-6xl px-4 py-8 scroll-mt-32">
      <h2 className="text-xl md:text-2xl font-bold text-foreground">حاسبة زكاة الذهب اليوم في السعودية</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        النصاب الشرعي للذهب هو 87.48 جراماً من الذهب الخالص (عيار 24). إذا ملكت هذا المقدار لمدة سنة هجرية كاملة، تجب الزكاة بنسبة 2.5%.
      </p>
      <div className="mt-5 grid gap-4 rounded-2xl border border-amber-400/30 bg-amber-400/10 p-5 sm:grid-cols-3">
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-semibold text-amber-700 dark:text-amber-200">وزن ذهبك (جرام)</span>
          <input
            type="number"
            min={0}
            value={grams}
            onChange={(e) => setGrams(Math.max(0, Number(e.target.value)))}
            className="rounded-lg border border-amber-400/30 bg-background px-3 py-2 text-base tabular-nums text-foreground outline-none focus:border-amber-400"
          />
        </label>
        <div className="text-xs">
          <div className="text-amber-700 dark:text-amber-200 font-semibold">قيمة النصاب اليوم</div>
          <div className="mt-1 text-lg font-extrabold tabular-nums text-foreground">{formatSarInt(nisabValue)}</div>
        </div>
        <div className="text-xs">
          <div className="text-amber-700 dark:text-amber-200 font-semibold">الزكاة المستحقة</div>
          <div className="mt-1 text-lg font-extrabold tabular-nums text-amber-600 dark:text-amber-300">
            {grams >= NISAB_G ? formatSar(zakatDue) : "لا تجب الزكاة (أقل من النصاب)"}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ----------------- FAQ ----------------- */
function FaqSection() {
  return (
    <section id="faq" className="mx-auto max-w-6xl px-4 py-8 scroll-mt-32">
      <h2 className="text-xl md:text-2xl font-bold text-foreground">الأسئلة الشائعة حول أسعار الذهب في السعودية</h2>
      <div className="mt-4 divide-y divide-amber-400/10 rounded-2xl border border-amber-400/20 bg-card">
        {SA_FAQS.map((f, i) => (
          <details key={i} className="group p-4">
            <summary className="cursor-pointer list-none text-sm font-semibold text-amber-700 dark:text-amber-200 marker:hidden flex justify-between gap-3">
              <span>{f.q}</span>
              <span className="text-amber-400 group-open:rotate-180 transition-transform">▾</span>
            </summary>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

/* ----------------- BUYING GUIDE ----------------- */
function BuyingGuide() {
  return (
    <section id="guide" className="mx-auto max-w-6xl px-4 py-8 scroll-mt-32">
      <h2 className="text-xl md:text-2xl font-bold text-foreground">دليل شراء الذهب في المملكة العربية السعودية</h2>
      <div className="mt-4 space-y-5 rounded-2xl border border-amber-400/20 bg-card p-5 text-sm leading-relaxed text-muted-foreground">
        <Para title="ما الفرق بين عيار 24 و 22 و 21 و 18؟">
          العيار يعبر عن نقاوة الذهب من أصل 24 جزءاً. عيار 24 يعني ذهباً خالصاً بنسبة 99.9% وهو الأنسب
          للسبائك والاستثمار لأنه يحافظ على القيمة كاملة عند البيع. عيار 22 (المعروف بـ 916) يحتوي على
          91.6% ذهب خالص مع 8.4% من المعادن الأخرى التي تعطيه قوة كافية للمشغولات اليومية. عيار 21 شائع
          جداً في دول الخليج ويحتوي على 87.5% ذهب، بينما عيار 18 يحتوي على 75% ذهب فقط ويستخدم
          للمجوهرات المرصعة بالألماس لأنه أكثر صلابة وقدرة على تثبيت الأحجار الكريمة.
        </Para>
        <Para title="كيف تتأكد من جودة الذهب قبل الشراء؟">
          ابحث دائماً عن الدمغة الحكومية المحفورة على القطعة (916 لعيار 22، 750 لعيار 18، 999 لعيار 24)،
          واطلب فاتورة رسمية تحدد الوزن والعيار وسعر الجرام وأجرة الصياغة بشكل منفصل. تجنب شراء الذهب
          من مصادر غير معتمدة، والتزم بالمحلات المرخصة من وزارة التجارة السعودية. عند الشك في القطعة،
          يمكنك الذهاب إلى مختبر معتمد من الهيئة السعودية للمواصفات والمقاييس والجودة لاختبار النقاوة.
        </Para>
        <Para title="أفضل الأوقات لشراء الذهب في السعودية">
          أفضل الأوقات للشراء تكون عند انخفاض سعر السبائك العالمي وارتفاع الدولار الأمريكي، وغالباً في
          أوائل الأسبوع وبعيداً عن مواسم الأعراس والأعياد حيث يرتفع الطلب وأجرة الصياغة. تجنب الشراء
          خلال ساعات الذروة في الأسواق العالمية (3 - 6 مساءً بتوقيت الرياض) حيث تكون التقلبات أكبر.
          راقب الرسم البياني لمدة أسبوع على الأقل قبل اتخاذ قرار الشراء الكبير، وقسم مشترياتك على
          فترات متعددة إذا كانت قيمة كبيرة لتقليل أثر التقلبات اليومية.
        </Para>
        <Para title="الذهب كاستثمار: لماذا يفضله السعوديون؟">
          الذهب يعتبر من أقدم وأقوى أدوات حفظ القيمة في الثقافة السعودية والإسلامية. على عكس العقارات
          والأسهم، الذهب سائل تماماً ويمكن تحويله إلى نقد في أي محل صاغة خلال دقائق. كما أنه يحمي
          المدخرات من التضخم العالمي ومن أي تقلبات في العملات الورقية. ينصح الخبراء الماليون بتخصيص
          ما بين 10% و 15% من المحفظة الاستثمارية للذهب الفعلي، مع تفضيل عيار 24 (سبائك ومسكوكات
          معتمدة) للاستثمار طويل الأمد لأنه لا يحمل أجرة صياغة كبيرة.
        </Para>
        <Para title="ضريبة القيمة المضافة والذهب في السعودية">
          تطبق هيئة الزكاة والضريبة والجمارك ضريبة القيمة المضافة 15% على المشغولات والمجوهرات
          الذهبية، بينما السبائك والمسكوكات الذهبية الاستثمارية بعيار 24 ذات الوزن المعتمد معفاة من
          الضريبة. هذا يجعل السبائك خياراً مفضلاً للمستثمرين الراغبين في تجنب التكاليف الإضافية.
          عند شراء مشغولات بقيمة كبيرة، تأكد من تضمين الضريبة في الفاتورة بشكل واضح وحساب التكلفة
          الإجمالية النهائية.
        </Para>
        <Para title="بيع الذهب المستعمل: كيف تحصل على أفضل سعر؟">
          عند بيع الذهب المستعمل أو الكسر، توجه إلى عدة محلات صاغة معتمدة للمقارنة قبل البيع. السعر
          يحسب حسب الوزن الفعلي بعد فحص العيار، مع خصم هامش بسيط (3 - 10 ريال للجرام) كربح للتاجر.
          تجنب بيع الذهب لمن يقدم أسعاراً أعلى بشكل غير منطقي، فقد يكون هناك خداع في الميزان أو في
          تحديد العيار. احتفظ بفواتير الشراء الأصلية إن أمكن لأنها تثبت العيار وقد تساعدك في الحصول
          على سعر أفضل.
        </Para>
        <Para title="زكاة الذهب: متى وكيف تجب؟">
          تجب زكاة الذهب إذا ملك الشخص نصاباً (87.48 جراماً من الذهب الخالص) أو ما يعادله من النقد
          والذهب مجتمعين لمدة سنة هجرية كاملة. مقدار الزكاة 2.5% من القيمة السوقية وقت إخراج الزكاة.
          استخدم حاسبة الزكاة في هذه الصفحة لمعرفة قيمة النصاب اليوم بالريال ومقدار الزكاة المستحقة
          بناءً على وزن ذهبك. ينصح بإخراج الزكاة في موعدها السنوي المحدد، ويمكن إخراجها نقداً أو بقيمة
          الذهب نفسه للجمعيات الخيرية المعتمدة في المملكة.
        </Para>
        <Para title="نصائح لشراء ذهب الزواج والأعراس">
          عند شراء ذهب الأعراس، خصص ميزانية واضحة قبل الذهاب للسوق، واختر عيار 21 أو 22 لأنهما الأنسب
          للاستخدام اليومي والاحتفاظ بالقيمة. تجنب التصاميم المعقدة جداً لأنها ترفع أجرة الصياغة بشكل
          كبير قد يصل إلى 30% من قيمة الذهب نفسه. قارن بين عدة محلات وتفاوض في أجرة الصياغة، فهي قابلة
          للتفاوض دائماً. اطلب شهادة ضمان كتابية تتضمن إمكانية استبدال القطعة بقيمتها المعدنية في حال
          الرغبة في التغيير مستقبلاً.
        </Para>
      </div>
    </section>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-amber-400/20 bg-card p-5">
      <div className="text-xs font-semibold text-amber-700 dark:text-amber-200">{title}</div>
      <div className="mt-2 text-sm text-muted-foreground leading-relaxed">{children}</div>
    </div>
  );
}

function Para({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-base font-bold text-amber-700 dark:text-amber-200 mb-1">{title}</h3>
      <p>{children}</p>
    </div>
  );
}